import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Eye, Stethoscope, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";

/**
 * ChartAccessCard — patient-facing transparency log.
 * Reads /me/chart-access to show who has viewed the user's chart recently.
 */
export default function ChartAccessCard() {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    api
      .get("/me/chart-access?limit=20")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded) return null;

  const preview = expanded ? rows : rows.slice(0, 3);

  return (
    <section
      data-testid="me-chart-access"
      className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="uppercase-label inline-flex items-center gap-1">
            <Eye size={14} /> Who viewed your chart
          </div>
          <p className="text-sm text-[color:var(--mano-ink-soft)] mt-2 max-w-lg">
            Every time a specialist or admin opens your record we note it here — for
            your peace of mind.
          </p>
        </div>
        {rows.length > 3 && (
          <button
            data-testid="me-chart-access-toggle"
            onClick={() => setExpanded((v) => !v)}
            className="inline-flex items-center gap-1 text-sm font-semibold text-[color:var(--mano-primary)]"
          >
            {expanded ? (
              <>Show less <ChevronUp size={14} /></>
            ) : (
              <>Show all {rows.length} <ChevronDown size={14} /></>
            )}
          </button>
        )}
      </div>

      {rows.length === 0 && (
        <div
          data-testid="me-chart-access-empty"
          className="mt-4 rounded-2xl border border-dashed border-[color:var(--mano-border)] p-5 text-sm text-[color:var(--mano-ink-soft)] inline-flex items-center gap-2"
        >
          <ShieldCheck size={14} className="text-[color:var(--mano-normal)]" />
          No one has opened your chart yet. It stays private until you book a consult.
        </div>
      )}

      {rows.length > 0 && (
        <ul className="mt-4 divide-y divide-[color:var(--mano-border)]">
          {preview.map((r) => (
            <li
              key={r.id}
              data-testid={`chart-access-${r.id}`}
              className="py-3 flex items-center gap-3 flex-wrap"
            >
              <div
                className={`h-9 w-9 rounded-xl flex items-center justify-center ${
                  r.actor_role === "platform_admin"
                    ? "bg-[color:var(--mano-ink)]/10 text-[color:var(--mano-ink)]"
                    : "bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)]"
                }`}
              >
                {r.actor_role === "platform_admin" ? (
                  <ShieldCheck size={16} />
                ) : (
                  <Stethoscope size={16} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{r.actor_name}</div>
                <div className="text-xs text-[color:var(--mano-ink-soft)] capitalize">
                  {roleLabel(r.actor_role)} · {friendlyTime(r.at)}
                </div>
              </div>
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[color:var(--mano-primary)] bg-[color:var(--mano-primary)]/10 px-2 py-1 rounded-full">
                Chart view
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function roleLabel(role) {
  if (role === "platform_admin") return "Platform admin";
  if (role === "clinical_specialist") return "Clinical specialist";
  if (role === "adjunctive_specialist") return "Adjunctive specialist";
  return role || "Viewer";
}

function friendlyTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} min ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} h ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD} d ago`;
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}
